import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleRegistryEntry, TenantSummary, page } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useListState } from '../../lib/useList';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import {
  Badge,
  Button,
  Card,
  CheckboxField,
  EmptyRow,
  FilterBar,
  PageHeader,
  Pager,
  SelectField,
  SortHeader,
  Spinner,
  TableWrap,
  TextField,
  Th,
} from '../../components/ui';

const TenantPage = page(TenantSummary);
const ModuleList = z.array(ModuleRegistryEntry);

export function tenantTone(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  return status === 'active' ? 'good' : status === 'suspended' ? 'warn' : 'neutral';
}

export function useModuleRegistry() {
  return useQuery({ queryKey: ['platform', 'modules'], queryFn: () => api.get('/platform/modules', { schema: ModuleList }) });
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function CreateTenant({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const modules = useModuleRegistry();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFullName, setAdminFullName] = useState('');
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const create = useMutation({
    mutationFn: () => api.post('/platform/tenants', { body: { name, slug, adminEmail, adminFullName, modules: [...chosen] }, schema: TenantSummary }),
    onSuccess: async (tn) => {
      await qc.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      onDone();
      void navigate(`/platform/tenants/${tn.id}`);
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  const optional = (modules.data ?? []).filter((m) => m.hasFeatureFlag);
  return (
    <Card title={t('tenants.create')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={create.error} />
        </div>
        <TextField
          label={t('common.name')}
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(slugify(e.target.value));
          }}
          error={errs['name']}
        />
        <TextField
          label={t('tenants.slug')}
          hint={t('tenants.slugHelp')}
          required
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
            setSlugTouched(true);
          }}
          error={errs['slug']}
        />
        <TextField label={t('tenants.adminEmail')} type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} error={errs['adminEmail']} />
        <TextField label={t('tenants.adminName')} required value={adminFullName} onChange={(e) => setAdminFullName(e.target.value)} error={errs['adminFullName']} />
        {optional.length > 0 ? (
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-sm font-medium">{t('tenants.modules')}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {optional.map((m) => (
                <CheckboxField
                  key={m.key}
                  label={t(m.nameKey)}
                  checked={chosen.has(m.key)}
                  onChange={(e) =>
                    setChosen((s) => {
                      const next = new Set(s);
                      if (e.target.checked) next.add(m.key);
                      else next.delete(m.key);
                      return next;
                    })
                  }
                />
              ))}
            </div>
          </fieldset>
        ) : null}
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {t('tenants.create')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function TenantsPage() {
  const list = useListState({ sort: 'name' });
  const canManage = useCan('platform.tenants_manage');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ['platform', 'tenants', { page: list.page, search: list.search, sort: list.sort, order: list.order, status }],
    queryFn: () =>
      api.get('/platform/tenants', {
        query: { page: list.page, pageSize: 20, search: list.search, sort: list.sort, order: list.order, status },
        schema: TenantPage,
      }),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <PageHeader
        title={t('nav.platform.tenants')}
        description={t('nav.platform.tenants.desc')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={creating} onClick={() => setCreating(true)}>
              {t('tenants.create')}
            </Button>
          ) : null
        }
      />
      {creating ? <CreateTenant onDone={() => setCreating(false)} /> : null}
      <FilterBar columns="sm:grid-cols-2">
        <TextField label={t('common.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('common.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('common.all')}</option>
          {(['active', 'suspended', 'archived'] as const).map((s) => (
            <option key={s} value={s}>
              {t(`status.${s}`)}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <TableWrap caption={t('nav.platform.tenants')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('common.name')} field="name" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <Th>{t('tenants.slug')}</Th>
                <SortHeader label={t('common.status')} field="status" sort={list.sort} order={list.order} onSort={list.toggleSort} />
                <Th>{t('tenants.users')}</Th>
                <SortHeader label={t('tenants.created')} field="createdAt" sort={list.sort} order={list.order} onSort={list.toggleSort} />
              </tr>
            </thead>
            <tbody>
              {q.data.items.length === 0 ? <EmptyRow cols={5} /> : null}
              {q.data.items.map((tn) => (
                <tr key={tn.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link className="font-medium text-brand hover:underline" to={`/platform/tenants/${tn.id}`}>
                      {tn.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-mono">{tn.slug}</td>
                  <td className="px-4 py-3">
                    <Badge tone={tenantTone(tn.status)}>{t(`status.${tn.status}`)}</Badge>
                  </td>
                  <td className="px-4 py-3">{tn.userCount}</td>
                  <td className="px-4 py-3">{formatDateTime(tn.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          <Pager page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} onPage={list.setPage} />
        </>
      )}
    </>
  );
}
